/* Timeline Journey — scroll-driven progressive fill of the timeline line.

   As the section scrolls through the viewport, the timeline line fills from the
   top downward with --timeline-bar-overlay-color. The fill is distributed across
   every vertical bar segment (.timeline__step::before) and every SVG branch
   (.timeline-split path.timeline-split__overlay) by setting a per-segment
   --seg-fill (0..1) custom property that the CSS consumes.

   On mobile (<750px) each .timeline__split is also wired up as a draggable
   horizontal scroller, mirroring the theme's DraggableSlider pattern used by
   grid--mobile-slider. */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------
     SplitSlider — mouse-drag-with-momentum horizontal scroller for
     .timeline__split elements on mobile. Mirrors the theme's DraggableSlider.
  -------------------------------------------------------------------------- */
  class SplitSlider {
    constructor(el) {
      this.el = el;
      this.isDown = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.velX = 0;
      this.scrollAnimation = null;
      this.isScrolling = false;
      this.duration = 800;

      this._onMouseDown = this._onMouseDown.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onMouseUp = this._onMouseUp.bind(this);
      this._onMouseMove = this._onMouseMove.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._scrollStep = this._scrollStep.bind(this);

      el.addEventListener('mousedown', this._onMouseDown);
      el.addEventListener('mouseleave', this._onMouseLeave);
      el.addEventListener('mouseup', this._onMouseUp);
      el.addEventListener('mousemove', this._onMouseMove);
      el.addEventListener('wheel', this._onWheel, { passive: true });

      el.classList.add('is-enabled');
    }

    _onMouseDown(e) {
      e.preventDefault();
      this.isDown = true;
      this.startX = e.pageX - this.el.offsetLeft;
      this.scrollLeft = this.el.scrollLeft;
      cancelAnimationFrame(this.scrollAnimation);
    }

    _onMouseLeave() {
      if (!this.isDown) return;
      this.isDown = false;
      this._beginMomentum();
    }

    _onMouseUp() {
      this.isDown = false;
      this._beginMomentum();
    }

    _onMouseMove(e) {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - this.el.offsetLeft;
      const walk = (x - this.startX);
      const prev = this.el.scrollLeft;
      const direction = walk > 0 ? 1 : -1;

      this.el.classList.add('is-dragging', 'is-scrolling');
      this.el.scrollLeft = this.scrollLeft - walk;

      if (this.el.scrollLeft !== prev) {
        this.velX = this.el.scrollLeft - prev || direction;
      }
    }

    _onWheel() {
      cancelAnimationFrame(this.scrollAnimation);
      this.el.classList.remove('is-scrolling');
    }

    _beginMomentum() {
      this.isScrolling = false;
      this.el.classList.remove('is-dragging');
      cancelAnimationFrame(this.scrollAnimation);
      this._scrollToSlide();
    }

    _scrollToSlide() {
      if (!this.velX && !this.isScrolling) return;

      const slide = this.el.querySelector('.timeline__split-step');
      if (!slide) return;

      const gap = parseInt(window.getComputedStyle(slide).marginRight) || 0;
      const slideWidth = slide.offsetWidth + gap;
      const direction = this.velX > 0 ? 1 : -1;
      const slidesToScroll = Math.floor(Math.abs(this.velX) / 100) || 1;

      // Snap to the nearest slide boundary
      const currentSlide = Math.round(this.el.scrollLeft / slideWidth);
      let targetSlide = currentSlide + (direction < 0 ? slidesToScroll : -slidesToScroll);
      targetSlide = Math.max(0, Math.min(targetSlide, this.el.querySelectorAll('.timeline__split-step').length - 1));

      this.startPosition = this.el.scrollLeft;
      this.distance = (targetSlide * slideWidth) - this.startPosition;
      this.startTime = performance.now();
      this.isScrolling = true;

      this.scrollAnimation = requestAnimationFrame(this._scrollStep);
    }

    _scrollStep() {
      const elapsed = performance.now() - this.startTime;
      const t = Math.min(elapsed, this.duration);
      this.el.scrollLeft = this._easeOutCubic(t);

      if (elapsed < this.duration) {
        this.scrollAnimation = requestAnimationFrame(this._scrollStep);
      } else {
        this.el.classList.remove('is-scrolling');
        this.velX = 0;
        this.isScrolling = false;
      }
    }

    _easeOutCubic(t) {
      t /= this.duration;
      t--;
      return this.distance * (t * t * t + 1) + this.startPosition;
    }

    destroy() {
      this.el.classList.remove('is-enabled', 'is-dragging', 'is-scrolling');
      this.el.removeEventListener('mousedown', this._onMouseDown);
      this.el.removeEventListener('mouseleave', this._onMouseLeave);
      this.el.removeEventListener('mouseup', this._onMouseUp);
      this.el.removeEventListener('mousemove', this._onMouseMove);
      this.el.removeEventListener('wheel', this._onWheel);
      cancelAnimationFrame(this.scrollAnimation);
    }
  }

  class TimelineJourney extends HTMLElement {
    constructor() {
      super();
      this.segments = [];
      this.rafId = null;
      this.splitSliders = [];
      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.measure.bind(this);
      this._onResizeSplit = this._toggleSplitSliders.bind(this);
    }

    connectedCallback() {
      this.steps = this.querySelector('.timeline__steps') || this;

      // Fraction of the viewport height (from the top) at which the fill line
      // sits. A smaller value means the overlay waits until a segment is nearer
      // the top of the screen before it starts filling. Defaults to 0.25.
      const attr = parseFloat(this.getAttribute('data-fill-threshold'));
      this.fillThreshold = Number.isFinite(attr) ? attr : 0.25;

      // Respect reduced-motion: fill instantly to full and skip listeners.
      this.reducedMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.collectSegments();
      this.measure();
      this._toggleSplitSliders();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('resize', this._onResizeSplit, { passive: true });

      // Recollect once images have loaded, since they affect segment heights.
      window.addEventListener('load', () => {
        this.onResize();
        if (window.innerWidth < 750) this._drawAllMobilePaths();
      }, { passive: true, once: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('resize', this._onResizeSplit);
      this._destroySplitSliders();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    /* Create or destroy SplitSlider instances depending on breakpoint. */
    _toggleSplitSliders() {
      const isMobile = window.innerWidth < 750;
      if (isMobile) {
        if (this.splitSliders.length === 0) {
          this.querySelectorAll('.timeline__split').forEach((el) => {
            this.splitSliders.push(new SplitSlider(el));
          });
        }
        this._drawAllMobilePaths();
      } else if (!isMobile && this.splitSliders.length > 0) {
        this._destroySplitSliders();
      }
    }

    _destroySplitSliders() {
      this.splitSliders.forEach((s) => s.destroy());
      this.splitSliders = [];
    }

    /* Draw the mobile SVG path for every .timeline__split-path on the page. */
    _drawAllMobilePaths() {
      this.querySelectorAll('.timeline__split-path').forEach((el) => {
        this._drawMobilePath(el);
      });
    }

    _drawMobilePath(pathEl) {
      const svg = pathEl.querySelector('.timeline__split-path__mobile-svg');
      if (!svg) return;

      const precedingStep = pathEl.previousElementSibling;
      if (!precedingStep || !precedingStep.classList.contains('timeline__step')) return;

      const splitEl = pathEl.nextElementSibling;
      if (!splitEl || !splitEl.classList.contains('timeline__split')) return;

      const steps = Array.from(splitEl.querySelectorAll('.timeline__split-step'));
      if (steps.length < 1) return;

      // Resolve rem and CSS custom properties to px.
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const stepsStyle = window.getComputedStyle(this.steps);
      const barWidth = parseFloat(stepsStyle.getPropertyValue('--timeline-bar')) || 4;
      const dotDiameter = parseFloat(stepsStyle.getPropertyValue('--timeline-bar-dot')) || 14;
      const dotBorder = parseFloat(stepsStyle.getPropertyValue('--timeline-bar-dot-border')) || 4;

      // Width of the SVG — matches .timeline__steps since both are full-width siblings.
      const svgW = this.steps.getBoundingClientRect().width;

      // Bar centre x: ::before is at left:0.5rem within the step.
      // pathEl and precedingStep share the same left edge inside .timeline__steps.
      const barCentreX = (0.5 * rem) + (barWidth / 2);

      // Dot centre x for each card.
      // On mobile h2::before has left:2rem and the dot centre is dotBorder + dotDiameter/2 further in.
      // Each step's left edge relative to pathEl: steps are inside splitEl which has
      // margin-left:-outer bleed, so use getBoundingClientRect to get viewport positions,
      // then subtract pathEl's left.
      const pathElLeft = pathEl.getBoundingClientRect().left;

      // Reset scroll so cards are at natural positions while we measure.
      const savedScroll = splitEl.scrollLeft;
      splitEl.scrollLeft = 0;

      const dotCentreInStep = (2 * rem) + dotBorder + (dotDiameter / 2);
      const dotXs = steps.map((step) => {
        const stepLeft = step.getBoundingClientRect().left;
        return stepLeft - pathElLeft + dotCentreInStep;
      });

      splitEl.scrollLeft = savedScroll;

      // Rail y — midpoint of the SVG height.
      const svgH = 56;
      const railY = svgH / 2;

      // Path: vertical drop → hard right → horizontal rail to last dot x.
      const lastDotX = dotXs[dotXs.length - 1];
      const d = `M ${barCentreX} 0 V ${railY} H ${lastDotX}`;

      svg.setAttribute('width', svgW);
      svg.setAttribute('height', svgH);
      svg.setAttribute('viewBox', `0 0 ${svgW} ${svgH}`);
      svg.querySelectorAll('path').forEach((p) => p.setAttribute('d', d));
    }

    /* Build the ordered list of fillable segments. Each segment is either a
       vertical bar host (a .timeline__step that renders a ::before bar) or an
       SVG overlay path. */
    collectSegments() {
      this.segments = [];

      const nodes = this.steps.querySelectorAll(
        '.timeline__step, .timeline-split__overlay'
      );

      nodes.forEach((node) => {
        if (node.classList.contains('timeline-split__overlay')) {
          this.segments.push({ type: 'path', el: node });
          return;
        }

        // Only steps whose ::before actually renders count as bar segments.
        const before = window.getComputedStyle(node, '::before');
        if (before && before.content && before.content !== 'none') {
          this.segments.push({ type: 'bar', el: node });
        }
      });
    }

    measure() {
      this.update();
      if (window.innerWidth < 750) this._drawAllMobilePaths();
    }

    requestUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      if (!this.segments.length) return;

      const viewportH = window.innerHeight || document.documentElement.clientHeight;

      // The fill line is a fixed threshold within the viewport. A segment only
      // begins to fill once it has scrolled up past this line, i.e. once some
      // of it is off the top of the page. The threshold is expressed as a
      // fraction of the viewport height from the top (0 = very top), and can be
      // tuned per-section via data-fill-threshold.
      const threshold = this.fillThreshold;
      const fillLineY = threshold * viewportH;

      this.segments.forEach((seg) => {
        const rect = seg.el.getBoundingClientRect();
        const segTop = rect.top;
        const segHeight = rect.height || 1;

        let fill = this.reducedMotion
          ? 1
          : (fillLineY - segTop) / segHeight;
        fill = Math.min(1, Math.max(0, fill));

        seg.el.style.setProperty('--seg-fill', fill);
      });
    }
  }

  if (!customElements.get('timeline-journey')) {
    customElements.define('timeline-journey', TimelineJourney);
  }
})();
